import { Controller } from 'stimulus';

// The readiness strip above a job's tasks (jobs/tasks/_readiness, where the
// new Schedule is on). It's fetched after the tab loads (Jobs::TasksController
// #readiness), since it plans the whole account. Use N Suggestions saves what
// past tasks of the same name suggest (the Schedule's plan_suggestions) and
// reloads the tab; the note and its Undo carry over the reload (session
// storage, this job only) and Undo posts the answer's undo list to plan_undo.
//
// So the tasks below don't jump while it loads, the strip from the last
// visit shows at once (session storage, this job, for ten minutes) and the
// fresh one replaces it; without one, a placeholder of the same height holds
// its place.
export default class extends Controller {
  static targets = ['holder', 'note', 'needs'];

  connect() {
    // (not on Turbolinks' cached preview: the real page follows and takes the note)
    if (document.documentElement.hasAttribute('data-turbolinks-preview')) return;
    this.holdPlace();
    this.load();
    // A row saved on the one view: the strip again, a moment later
    // (a reminder changes nothing the planner reads)
    this.changed = (e) => {
      if (e.detail && e.detail.field === 'reminder') return;
      clearTimeout(this.again);
      this.again = setTimeout(() => this.load(), 400);
    };
    this.filtered = (e) => { this.filterOn = !!(e.detail && e.detail.on); this.markFilter(); };
    document.addEventListener('tasks:changed', this.changed);
    document.addEventListener('tasks:needs-filter', this.filtered);
  }

  disconnect() {
    clearTimeout(this.again);
    document.removeEventListener('tasks:changed', this.changed);
    document.removeEventListener('tasks:needs-filter', this.filtered);
  }

  // What the tasks need, as a toggle: only those tasks (tasks-one#filter)
  filter(e) {
    e.preventDefault();
    const ids = (e.currentTarget.dataset.ids || '').split(',').filter(Boolean);
    document.dispatchEvent(new CustomEvent('tasks:needs-filter', { detail: { on: !this.filterOn, ids } }));
  }

  markFilter() {
    if (this.hasNeedsTarget) this.needsTarget.classList.toggle('is-on', !!this.filterOn);
  }

  get key() { return `task-readiness-note-${this.data.get('job')}`; }

  get stripKey() { return `task-readiness-strip-${this.data.get('job')}`; }

  // The last visit's strip, else a placeholder its height, until the fresh one comes
  holdPlace() {
    let cached = null;
    try { cached = JSON.parse(window.sessionStorage.getItem(this.stripKey) || 'null'); } catch (e) { cached = null; }
    if (cached && cached.html && Date.now() - (cached.at || 0) < 600000) {
      this.holderTarget.innerHTML = cached.html;
      this.markFilter();
    } else {
      this.holderTarget.innerHTML = '<div class="tr-strip tr-strip--loading" aria-busy="true"><span class="tr-part tr-muted">Checking when the Schedule plans this job…</span></div>';
    }
  }

  remember(html) {
    try {
      if (html) window.sessionStorage.setItem(this.stripKey, JSON.stringify({ html, at: Date.now() }));
      else window.sessionStorage.removeItem(this.stripKey);
    } catch (e) { /* a convenience */ }
  }

  load() {
    const saved = this.takeSaved();
    fetch(this.data.get('stripUrl'), { credentials: 'same-origin', headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok && r.status !== 204 && !r.redirected ? r.text() : ''))
      .then((html) => {
        if (html.indexOf('tr-strip') < 0) {
          // (no strip for this job: the placeholder or the old one goes)
          this.holderTarget.innerHTML = '';
          this.remember(null);
          return;
        }
        this.holderTarget.innerHTML = html;
        this.remember(html);
        this.showSaved(saved);
        if (this.filterOn && this.hasNeedsTarget) {
          // (still filtering: the rows follow what the fresh strip says)
          this.markFilter();
          const ids = (this.needsTarget.dataset.ids || '').split(',').filter(Boolean);
          document.dispatchEvent(new CustomEvent('tasks:needs-filter', { detail: { on: true, ids } }));
        } else if (this.filterOn) {
          document.dispatchEvent(new CustomEvent('tasks:needs-filter', { detail: { on: false, ids: [] } }));
        }
      })
      .catch(() => {});
  }

  // A note left by the save (or Undo) just before the reload; taken whether
  // or not the strip comes back, and only while it's fresh
  takeSaved() {
    let saved = null;
    try { saved = JSON.parse(window.sessionStorage.getItem(this.key) || 'null'); window.sessionStorage.removeItem(this.key); } catch (e) { saved = null; }
    return saved && Date.now() - (saved.at || 0) < 60000 ? saved : null;
  }

  showSaved(saved) {
    if (!saved || !this.hasNoteTarget) return;
    this.undoItems = saved.undo && saved.undo.length ? saved.undo : null;
    this.noteTarget.innerHTML = this.escape(saved.text) + (this.undoItems ? ' <button type="button" class="link focus:outline-none" data-action="task-readiness#undo">Undo</button>' : '');
    this.noteTarget.hidden = false;
  }

  use(e) {
    e.preventDefault();
    const btn = e.currentTarget;
    if (btn.disabled) return;
    btn.disabled = true;
    this.request(this.data.get('url'), 'PATCH', { task_ids: btn.dataset.tasks.split(',') })
      .then((json) => this.reloadWith((json.changes || []).join(' '), json.undo))
      .catch((err) => { btn.disabled = false; this.problem(err.message); });
  }

  undo(e) {
    e.preventDefault();
    if (!this.undoItems) return;
    const items = this.undoItems;
    this.undoItems = null;
    this.request(this.data.get('undoUrl'), 'POST', { undo: items })
      .then(() => this.reloadWith('Undone.', null))
      .catch((err) => this.problem(err.message));
  }

  request(url, method, body) {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return fetch(url, {
      method,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': meta ? meta.content : '' },
      body: JSON.stringify(body)
    })
      .then((r) => r.json().catch(() => ({ ok: false })))
      .then((json) => {
        if (!json.ok) throw new Error(json.error || 'That could not be saved.');
        return json;
      });
  }

  reloadWith(text, undo) {
    try { window.sessionStorage.setItem(this.key, JSON.stringify({ text, undo, at: Date.now() })); } catch (e) { /* the note is a convenience */ }
    // (the answers just saved change what it says: no old strip after the reload)
    this.remember(null);
    if (window.Turbolinks) window.Turbolinks.visit(window.location.href, { action: 'replace' });
    else window.location.reload();
  }

  problem(text) {
    if (!this.hasNoteTarget) return;
    this.noteTarget.innerHTML = `<span class="tr-err">${this.escape(text || 'That could not be saved.')}</span>`;
    this.noteTarget.hidden = false;
  }

  escape(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
